/**
 * Tharwy Learn — plain-English explainers written for clients, not for scholars.
 * Content is original and deliberately unhurried: no jargon without a definition.
 */
export const ARTICLES = [
  {
    slug: "murabaha-savings-explained",
    title: "Murabaha savings, explained without the jargon",
    dek: "Where your money actually goes when you choose an Islamic savings plan, and why the return is a profit rather than interest.",
    category: "Islamic finance",
    read: 6,
    date: "2026-06-02",
    sections: [
      {
        h: "The problem Murabaha solves",
        p: [
          "A conventional savings account pays you for the use of your money. The bank lends it out, earns interest, and passes some back. The mechanism is a loan, and the return on a loan is riba — the thing Islamic finance exists to avoid.",
          "Murabaha replaces the loan with a trade. Instead of renting out your cash, the institution uses it to buy something real, then sells that thing on at a disclosed markup. Your return is a share of a trading profit, and profit is permissible.",
        ],
      },
      {
        h: "What happens, step by step",
        list: [
          "You commit an amount and a term — say $5,000 for 90 days.",
          "The desk buys a real commodity at a spot price: aluminium, copper, palm oil, wheat.",
          "It immediately sells that commodity to a pre-agreed buyer at cost plus a fixed markup.",
          "The buyer settles on the maturity date, and your capital plus your share of the markup is credited to your wallet.",
        ],
      },
      {
        h: "Why the rate looks like a rate",
        p: [
          "The markup is fixed at the outset, so the return often lands close to a conventional deposit rate. That resemblance makes people suspicious, and fairly so — but the resemblance is in the number, not in the structure.",
          "In a Murabaha the profit comes from a sale of goods that were genuinely owned before they were sold. Ownership carries risk, however briefly, and it is that risk which earns the return. Interest is charged on money that was never at risk at all.",
        ],
      },
      {
        h: "What to look for before you commit",
        list: [
          "Can you see the commodity, the purchase price and the markup? If the paperwork is vague, walk away.",
          "Is the markup fixed, or does it float with a benchmark? Floating markups start to behave like interest.",
          "What happens if the buyer defaults? A genuine Murabaha exposes you to a real, if small, commercial risk.",
          "Is there a penalty for late settlement that becomes income for the desk? That penalty should go to charity, not to profit.",
        ],
      },
    ],
    takeaways: [
      "Your return comes from a trade, not from lending money.",
      "A fixed, disclosed markup is the hallmark of a clean Murabaha.",
      "Similar numbers do not mean an identical structure — read the contract, not the headline rate.",
    ],
  },
  {
    slug: "interest-vs-profit",
    title: "Interest and profit: same number, different journey",
    dek: "Two returns can look identical on a statement and still be worlds apart in how they were earned.",
    category: "Islamic finance",
    read: 5,
    date: "2026-05-18",
    sections: [
      {
        h: "The test is the risk, not the rate",
        p: [
          "Islamic finance does not object to making money. It objects to making money without carrying any of the consequences. Interest is guaranteed the moment the loan is signed: whatever happens to the borrower, the lender is owed the same amount.",
          "Profit has to be earned. Someone has to own an asset, bear the chance that it falls in value, and only then sell it on. That exposure — however short — is what turns a return from riba into trade.",
        ],
      },
      {
        h: "Three questions that separate them",
        list: [
          "Was something real owned before it was sold? Money renting money fails this test.",
          "Could the provider have lost? If the answer is never, the return is interest with better branding.",
          "Is the payment tied to time, or to a transaction? Charges that accrue purely with the calendar are the signature of a loan.",
        ],
      },
      {
        h: "Where it shows up in a portfolio",
        p: [
          "Equity passes easily: you own a slice of a business and rise or fall with it. Sukuk pass when they represent ownership of an asset that generates rent, rather than a promise to repay a debt.",
          "Cash is where most portfolios quietly fail. Idle balances in an interest-bearing account earn riba by default — which is why Tharwy holds client cash in non-interest-bearing accounts and offers a Murabaha plan for those who want their cash working.",
        ],
      },
    ],
    takeaways: [
      "Guaranteed return with zero exposure is the definition of interest.",
      "Ownership, even for minutes, is what makes a profit permissible.",
      "Check your cash, not just your investments — that is where riba usually hides.",
    ],
  },
  {
    slug: "aaoifi-screens-explained",
    title: "The AAOIFI screens, one threshold at a time",
    dek: "Why a profitable, ethical-looking company can still fail a Shariah screen — and what each ratio is really measuring.",
    category: "Screening",
    read: 7,
    date: "2026-05-04",
    sections: [
      {
        h: "First gate: what the business does",
        p: [
          "Before any number is looked at, the company's line of business is tested. Conventional banking and insurance, alcohol, tobacco, gambling, adult entertainment, pork and weapons are excluded outright. No ratio can rescue a business whose core activity is impermissible.",
          "Most companies clear this gate easily. A supermarket that sells some alcohol is judged on how much of its revenue that represents, not on the fact that it appears on a shelf.",
        ],
      },
      {
        h: "Second gate: three financial ratios",
        list: [
          "Interest-bearing debt below 30% of market capitalisation. A company financed largely by conventional loans passes its riba on to you as a shareholder.",
          "Non-permissible income below 5% of total revenue. This covers interest earned on deposits and any incidental impermissible activity.",
          "Illiquid assets above 20% of total assets. If a company is mostly cash and receivables, buying its shares starts to resemble trading money for money.",
        ],
      },
      {
        h: "Why verdicts change",
        p: [
          "These are ratios, not fixed traits. A company that buys back stock with borrowed money can cross the 30% debt line in a single quarter. A cash-rich technology firm can drift over the 5% line simply because deposit rates rose.",
          "That is why a screen is only as good as its last refresh. Tharwy re-screens every holding on a 30-day cycle against the latest filings, and tells you which specific threshold moved.",
        ],
      },
      {
        h: "Failing is not always selling",
        p: [
          "A 'Questionable' verdict is information, not an instruction. Many scholars permit continuing to hold a marginal name while purifying the tainted share of the income. A clear 'Non-Compliant' verdict — especially on business activity — usually means exiting in an orderly way.",
          "What matters is that the decision is yours, taken with the numbers in front of you.",
        ],
      },
    ],
    takeaways: [
      "Business activity is tested first; ratios only decide borderline cases.",
      "30% debt, 5% impermissible income, 20% illiquid assets are the thresholds to remember.",
      "Verdicts move with the balance sheet — screening once is not screening.",
    ],
  },
  {
    slug: "dividend-purification",
    title: "Dividend purification: how much to give, and why",
    dek: "A small part of most dividends was never yours to keep. Here is how the share is calculated and what to do with it.",
    category: "Screening",
    read: 5,
    date: "2026-04-21",
    sections: [
      {
        h: "Where the impurity comes from",
        p: [
          "Even a compliant company usually earns a sliver of income from something impermissible — typically interest on its bank deposits. When it pays a dividend, that sliver comes along for the ride.",
          "Purification is the practice of calculating your share of that sliver and giving it away, so the income you keep is clean. It is not a fine, and it does not count as charity for reward — it is a return of something that was not yours.",
        ],
      },
      {
        h: "The calculation",
        p: [
          "The purification factor is the proportion of the company's revenue that came from impermissible sources. Multiply your dividend by that percentage and you have the amount to give.",
          "If a company earned 1.2% of its revenue from interest and paid you $200 in dividends, $2.40 needs to leave your pocket. Small per holding; not small across a portfolio held for a decade.",
        ],
      },
      {
        h: "How to give it",
        list: [
          "Give it to charity with no expectation of reward or tax benefit.",
          "Keep a simple record — Tharwy tracks the amount owed and the amount already given, per year.",
          "Purify capital gains too if your scholar's view requires it; the same factor is normally applied.",
        ],
      },
    ],
    takeaways: [
      "Purification cleans income; zakat is a separate obligation on wealth.",
      "Multiply each dividend by the holding's purification factor.",
      "Track it yearly — the Shariah Watch page keeps the running total for you.",
    ],
  },
  {
    slug: "zakat-on-shares",
    title: "Zakat on shares, honestly calculated",
    dek: "Whether you are a trader or a long-term investor changes what you owe. Both methods, worked through.",
    category: "Zakat",
    read: 6,
    date: "2026-04-07",
    sections: [
      {
        h: "Start with your intention",
        p: [
          "If you bought shares to sell them at a profit, the market value of the whole holding is zakatable, like trade goods in a shop.",
          "If you bought them to hold for the dividends, most scholars ask you to pay zakat only on the zakatable assets inside the company — its cash, receivables and inventory — not on its factories and machinery.",
        ],
      },
      {
        h: "The two calculations",
        list: [
          "Trader's method: 2.5% of the market value of your portfolio on your zakat date.",
          "Investor's method: 2.5% of your share of the company's zakatable assets — commonly approximated at 25–30% of market value where full accounts are not available.",
        ],
      },
      {
        h: "Details people miss",
        list: [
          "Cash balances, savings plans and Murabaha positions are fully zakatable at market value.",
          "Debts you are owed and expect to receive are added; immediate liabilities can be deducted.",
          "Use one date every year — the Islamic calendar is preferred — and stick to it, so nothing is counted twice or missed.",
          "Zakat is due on wealth held for a full lunar year above the nisab, measured against the gold or silver threshold.",
        ],
      },
    ],
    takeaways: [
      "Your intention when buying decides which method applies.",
      "Cash and savings are always zakatable in full.",
      "Fix one annual date and calculate everything against it.",
    ],
  },
  {
    slug: "dividend-withholding-tax",
    title: "Why your dividend arrived smaller than expected",
    dek: "Withholding tax is deducted in the country that pays the dividend, long before it reaches your wallet. What each market keeps, and what you can claim back.",
    category: "Global investing",
    read: 6,
    date: "2026-03-24",
    sections: [
      {
        h: "The deduction happens at source",
        p: [
          "When a US company pays a dividend, the US takes its cut first. Your broker never sees the gross amount — the paying agent deducts the tax and forwards the remainder. Nothing on your side can change that after the fact.",
          "This is why Tharwy credits the net amount and shows the gross, the tax and the country on the same line. Anything else would flatter the return.",
        ],
      },
      {
        h: "Rates vary wildly",
        list: [
          "United States: 30% statutory for non-residents, often 15% under a treaty.",
          "Switzerland: 35%, among the highest, with a reclaim process.",
          "Germany: 26.375%. France: 25%. Japan: 15.315%.",
          "United Kingdom, UAE, Qatar, Kuwait, Malaysia, Singapore, Brazil: 0%.",
          "Saudi Arabia: 5%. India: 20%. South Africa: 20%.",
        ],
      },
      {
        h: "What you can do about it",
        p: [
          "Two levers exist. The first is documentation: filing the right residency form with the paying agent can cut the US rate from 30% to the treaty rate of 15%, which doubles your net income from US dividend payers over time.",
          "The second is selection. If two holdings offer the same gross yield and one is domiciled in a zero-withholding market, the after-tax winner is not a close call. Accumulating Irish-domiciled ETFs and Gulf-listed names are common answers.",
        ],
      },
      {
        h: "Keep the paperwork",
        p: [
          "Tax withheld abroad can sometimes be credited against tax owed at home. That requires evidence, per payment, per country — which is exactly what the downloadable dividend statement in Tharwy is for.",
        ],
      },
    ],
    takeaways: [
      "You are always paid net; the gross figure is only ever informational.",
      "A residency form can halve the US rate — it is the highest-return paperwork in investing.",
      "Domicile matters as much as yield when you compare income holdings.",
    ],
  },
];

export const CATEGORIES = ["All", ...Array.from(new Set(ARTICLES.map((a) => a.category)))];
export const bySlug = (slug) => ARTICLES.find((a) => a.slug === slug);
